import { ArrowDownToLine, Replace, ListPlus, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import type { OfficeHost } from '../office/bootstrap';
import type { InsertMode } from '../office/context';
import { renderMarkdown } from '../lib/markdown';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  /** Optional badge — e.g. agent name(s) that produced the answer. */
  badge?: string;
  /** True while the assistant message is still streaming/loading. */
  pending?: boolean;
  /** Reasoning / orchestrator transcript for the curious. */
  reasoning?: string;
}

interface Props {
  msg: ChatMessage;
  host: OfficeHost;
  onInsert: (mode: InsertMode, text: string, html?: string) => void;
}

export function MessageBubble({ msg, host, onInsert }: Props) {
  const [showReasoning, setShowReasoning] = useState(false);
  const [copied, setCopied] = useState(false);

  if (msg.role === 'system') {
    return (
      <div className="my-2 text-center text-[11px] text-ink-500">{msg.text}</div>
    );
  }

  const isUser = msg.role === 'user';
  const html = !isUser && msg.text ? renderMarkdown(msg.text) : '';

  function copy() {
    navigator.clipboard.writeText(msg.text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  }

  return (
    <div className={`mb-2 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[92%] rounded-2xl px-3 py-2 text-sm shadow-soft ${
          isUser
            ? 'rounded-br-sm bg-brand-500 text-white'
            : 'rounded-bl-sm bg-white text-ink-900'
        }`}
      >
        {msg.badge && !isUser && (
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-brand-500">
            {msg.badge}
          </div>
        )}

        {msg.pending ? (
          <div className="flex items-center gap-1 py-1">
            <span className="ac-dot inline-block h-1.5 w-1.5 rounded-full bg-ink-300" />
            <span className="ac-dot inline-block h-1.5 w-1.5 rounded-full bg-ink-300" />
            <span className="ac-dot inline-block h-1.5 w-1.5 rounded-full bg-ink-300" />
          </div>
        ) : isUser ? (
          <div className="whitespace-pre-wrap">{msg.text}</div>
        ) : (
          <div className="md" dangerouslySetInnerHTML={{ __html: html }} />
        )}

        {!isUser && !msg.pending && msg.text && (
          <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-ink-100 pt-1.5">
            {host !== 'Teams' && host !== 'Meet' && host !== 'Unknown' && (
              <ActionBtn
                onClick={() => onInsert(host === 'Excel' ? 'insert' : 'insert', msg.text, html)}
                icon={<ArrowDownToLine className="h-3 w-3" />}
                label={host === 'Excel' ? 'Write to cells' : 'Insert'}
              />
            )}
            {host === 'Word' && (
              <>
                <ActionBtn
                  onClick={() => onInsert('replace', msg.text, html)}
                  icon={<Replace className="h-3 w-3" />}
                  label="Replace"
                />
                <ActionBtn
                  onClick={() => onInsert('append', msg.text, html)}
                  icon={<ListPlus className="h-3 w-3" />}
                  label="Append"
                />
              </>
            )}
            <ActionBtn
              onClick={copy}
              icon={copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              label={copied ? 'Copied' : 'Copy'}
            />
            {msg.reasoning && (
              <button
                type="button"
                onClick={() => setShowReasoning((v) => !v)}
                className="ml-auto text-[10px] uppercase tracking-wide text-ink-500 hover:text-brand-500"
              >
                {showReasoning ? 'Hide reasoning' : 'Why this answer?'}
              </button>
            )}
          </div>
        )}

        {showReasoning && msg.reasoning && (
          <div className="mt-2 whitespace-pre-wrap rounded-md bg-ink-100/60 p-2 text-[11px] text-ink-700">
            {msg.reasoning}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionBtn({
  onClick,
  icon,
  label,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-ink-500 hover:bg-brand-50 hover:text-brand-700"
    >
      {icon}
      {label}
    </button>
  );
}
